import {
  NgModule,
  APP_INITIALIZER,
  LOCALE_ID,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { OverlayModule } from '@angular/cdk/overlay';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// RxJs
import { lastValueFrom } from 'rxjs';

// Forms
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Environment
import { environment } from '@environments/environment';

// Config
import { initializeKeycloak } from '@core/config/initializer';

// Keycloak
import {
  KeycloakService,
  KeycloakAngularModule,
  KeycloakBearerInterceptor,
} from 'keycloak-angular';

// Translate library
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';

// Translate loader
import { ModuleTranslationsLoader } from '@core/loaders/module-translations-loader';

// APIs
import { AUTH_API_BASE_URL } from '@tech/api-clients/auth';
import {
  MODULES_API_BASE_URL,
  LanguagesClient,
} from '@tech/api-clients/modules';
import {
  TRANSLATIONS_API_BASE_URL,
  TranslationsClient,
} from '@tech/api-clients/translations';

// Store
import { NgRxModule } from '@core/state/ngrx.module';

// TECH Components
import { TechIconModule } from '@tech/components/tech-icon';
import { TechScrollbarModule } from '@tech/components/tech-scrollbar';
import { HR_API_BASE_URL } from '@tech/api-clients/hr';
import { API_BASE_URL } from '@tech/api-clients/receipts';

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    OverlayModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    KeycloakAngularModule,
    NgRxModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (translationsClient: TranslationsClient) =>
          new ModuleTranslationsLoader(
            translationsClient,
            environment.moduleCode
          ),
        deps: [TranslationsClient],
      },
      defaultLanguage: 'es',
      useDefaultLang: true,
    }),
    TechIconModule.forRoot({
      appearance: 'outlined',
      fill: true,
      weight: '500',
      grade: '0',
      opticalSize: 24,
    }),
    TechScrollbarModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: API_BASE_URL,
      useValue: environment.apiUrl,
    },
    {
      provide: MODULES_API_BASE_URL,
      useFactory: () => environment.modulesApiUrl,
    },
    {
      provide: AUTH_API_BASE_URL,
      useFactory: () => environment.authApiUrl,
    },
    {
      provide: TRANSLATIONS_API_BASE_URL,
      useFactory: () => environment.translationsApiUrl,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (languageClient: LanguagesClient) => async () => {
        const languages = await lastValueFrom(languageClient.getLanguages());

        for (const l of languages) {
          if (l.localeCode) {
            const locale = await import(
              `../../../node_modules/@angular/common/locales/${l.localeCode}.mjs`
            );
            registerLocaleData(locale.default);
          }
        }
      },
      multi: true,
      deps: [LanguagesClient],
    },
    {
      provide: LOCALE_ID,
      useFactory: (translateService: TranslateService) =>
        translateService.getDefaultLang(),
      deps: [TranslateService],
    },
    DatePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CoreModule {}
